<script>
  import katexify from "../katexify";
  import { selectAll } from "d3-selection";
  import Scatterplot from "./Scatterplot.svelte";
  import {
    sqft,
    lineType,
    coeff,
    intercept,
    showRegressionLine,
    showResiduals,
    showHighlight,
  } from "../store.js";
  import { onMount } from "svelte";
  import { format } from "d3-format";

  const formatter = format("$,");

  let scatterClass;

  // let sections;
  const target2event = {
    0: () => {
      scatterClass.hideAnnotationLines();
      $lineType = "regressionLineFlat";
      $showRegressionLine = true;
      $showResiduals = false;
      $coeff = 0;
      $intercept = 293683;
    },
    1: () => {
      $lineType = "regressionLineFlat";
      $showRegressionLine = true;
      $showResiduals = true;
    },

    2: () => {
      scatterClass.hideAnnotationLines();
      $lineType = "regressionLine";
      $coeff = 756.9;
      $intercept = -27153.8;
      $showResiduals = true;
      $showRegressionLine = true;
      $showHighlight = false;
    },
    3: () => {
      $showHighlight = true;
      scatterClass.showAnnotationLines();
      $showResiduals = false;
      $showRegressionLine = true;
      $lineType = "regressionLine";
      $coeff = 756.9;
      $intercept = -27153.8;
    },
    4: () => {
      $showResiduals = false;
      $showRegressionLine = true;
      $coeff = 0.097;
      $intercept = -27153.8;
      $lineType = "regressionLineSqrt";
    },
    5: () => {},
  };

  function fireEvent(entryIndex) {
    if (entryIndex in target2event) {
      target2event[entryIndex]();
    }
  }

  onMount(() => {
    // store elements to track
    let sections = selectAll(".step").nodes();

    // observe elements to track
    sections.forEach((section) => {
      observer.observe(section);
    });
  });

  // options for intersection observer
  const options = {
    threshold: 0.7,
  };

  let observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      // check if visible or not
      if (entry.isIntersecting) {
        // resolve stage in graph
        const entryIndex = entry.target.getAttribute("data-index");
        if (entryIndex in target2event) {
          fireEvent(entryIndex);
        }
      }
    });
  }, options);

  // $: if (typeof value !== "undefined") target2event[value]();
</script>

<h2 class="body-header">How It Works, Briefly</h2>
<p class="body-text">
  Before we get lost in the math, let's look at the intuition behind Linear Regression.
  Scroll through the steps below to see how the algorithm "learns" from data:
</p>
<br />
<section id="scrolly">
  <div class="scrolly-container">
    <div class="steps-container">
      <div class="step" data-index="0">
        <div class="step-content">
          <p>
            Imagine we want to predict <b>house prices</b> in San Diego based on the 
            <b>size of the house</b> (square footage).
            <br /><br />
            {@html katexify(
              `\\text{Price} = \\hat{\\beta_1} * \\text{Size} + \\hat{\\beta_0}`,
              false
            )}
            <br /><br />
            Let's start with the simplest guess possible: predicting that <i>every</i> 
            house costs the average price (~$290,000), regardless of how big or small it is.
            <br /><br />
            {@html katexify(`\\text{Price} = 0 * \\text{Size} + 290000`, false)}
          </p>
        </div>
      </div>
      <div class="step" data-index="1">
        <div class="step-content">
          <p>
            Obviously, that model is bad—a tiny studio shouldn't cost the same as a mansion. 
            But <i>how</i> bad is it?
            <br /><br />
            To measure this, we look at the <b>residuals</b> (errors). A residual is simply 
            the distance between the <i>actual</i> price (the dot) and our <i>predicted</i> 
            price (the line).
            <br /><br />
            In the chart, you can see these represented as lines connecting the data points 
            to our flat prediction line. Long lines mean big errors.
          </p>
        </div>
      </div>
      <div class="step" data-index="2">
        <div class="step-content">
          <p>
            The goal of Linear Regression is essentially a game: move the line until 
            the total length of those error lines is as small as possible.
            <br /><br />
            By adjusting the position (intercept, {@html katexify(`\\hat{\\beta_0}`, false)}) 
            and angle (slope, {@html katexify(`\\hat{\\beta_1}`, false)}) of the line, 
            we find the <b>"Line of Best Fit."</b>
            <br /><br />
            It won't hit every dot perfectly, but it captures the trend: bigger houses cost more.
          </p>
        </div>
      </div>
      <div class="step" data-index="3">
        <div class="step-content">
          <p>
            Now that we have our best-fit line, predicting the future is easy! We just 
            plug a new square-footage number ({@html katexify(`x_i`, false)}) into our equation.
            <br /><br />
            Try using the slider to see how the model predicts the price for different house sizes:
          </p>
          <br />
          <div id="input-container">
            <p>{@html katexify(`sqft`, false)} Value: {$sqft}</p>
            <input
              type="range"
              min="36"
              max="775"
              step="1"
              bind:value={$sqft}
              class="slider"
              id="myRange"
            />
          </div>
          <p>
            <br />
            {@html katexify(
              `\\hat{y} = ${$coeff} * ${$sqft} ${$intercept}`,
              false
            )}
            <br />
            {@html katexify(
              `\\hat{y} =  ${Math.round($sqft * $coeff + $intercept, 3)}`,
              false
            )}
            <br />
            <br />
            Our model predicts a house of <b>{$sqft} square-feet</b> will cost
            <b>{formatter(Math.round($sqft * $coeff + $intercept, 3))}</b>.
          </p>
        </div>
      </div>
      <div class="spacer" />
    </div>
    <div class="charts-container">
      <div class="chart-one">
        <Scatterplot bind:this={scatterClass} />
      </div>
    </div>
  </div>

  <br /><br />

  <p class="body-text">
    Now that you have the intuition, let's get into the mechanics. The rest of 
    this article will cover how we mathematically find that "best" line, how to 
    grade our model's performance, and the rules we need to follow to use it correctly.
    <br /><br /> Let's dive in!
  </p>
</section>

<style>
  #scrolly {
    max-width: 1500px;
    margin: auto;
  }
  .chart-one {
    width: 100%;
    height: 100%;
  }
  /* space after scroll is finished */
  .spacer {
    height: 40vh;
  }

  /* .charts-container {
    position: sticky;
    top: 20%;
    width: 40%;
    height: 40vh;
    margin-right: 5%;
  } */

  .charts-container {
    position: sticky;
    top: 20%;
    width: 40%;
    height: 40vh;
    margin-right: 5%;
  }

  .scrolly-container {
    margin-top: 1em;
    text-align: center;
    transition: background 100ms;
    display: flex;
  }

  .step {
    height: 110vh;
    display: flex;
    place-items: center;
    justify-content: center;
  }

  .step-content {
    font-size: var(--size-default);
    background: var(--bg);
    border-radius: 1px;
    padding: 0.5rem 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    transition: background 500ms ease;
    text-align: left;
    width: 75%;
    margin: auto;
    max-width: 500px;
    font-family: var(--font-main);
    line-height: 1.3;
    border: 4px solid var(--default);
  }

  .step-content p {
    color: var(--squidink);
  }

  .steps-container {
    height: 100%;
  }

  .steps-container {
    flex: 1 1 40%;
    z-index: 10;
  }

  /* Comment out the following line to always make it 'text-on-top' */
  @media screen and (max-width: 950px) {
    .scrolly-container {
      flex-direction: column-reverse;
    }

    .steps-container {
      pointer-events: none;
    }

    .charts-container {
      top: 20.5%;
      width: 95%;
      margin: auto;
    }

    .step {
      height: 130vh;
    }

    .step-content {
      width: 95%;
      max-width: 768px;
      font-size: 15px;
      line-height: 1.3;
      background: rgba(241, 243, 243, 0.913);
    }

    .spacer {
      height: 100vh;
    }
  }
</style>
